import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    Injector,
    input,
    linkedSignal,
    output,
    runInInjectionContext,
    Signal,
    signal,
    untracked,
    ViewEncapsulation,
    WritableSignal,
} from "@angular/core";
import { CardReaderService, CardSwipe } from "../../../services/card.reader.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { filter } from "rxjs";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { applyEach, FormField, FieldTree, form, hidden, validate, ValidationError } from "@angular/forms/signals";
import { S25ToggleButtonModule } from "../../s25-toggle-button/s25.toggle.button.module";
import { S25Util } from "../../../util/s25-util";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25CheckboxModule } from "../../s25-checkbox/s25.checkbox.module";
import { S25IconButtonComponent } from "../../../standalone/s25.icon.button.component";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { NotificationService } from "../../../services/notification.service";
import { S25Const } from "../../../util/s25-const";
import { IdCardsStore } from "./id.cards.store";
import { ReactiveFormsModule } from "@angular/forms";
import { S25RadioModule } from "../../s25-radio/s25.radio.module";
import { ModalService } from "../../modal/modal.service";
import { S25InfoMessageModule } from "../../s25-info-message/s25.info.message.module";
import { Bind } from "../../../decorators/bind.decorator";
import { IdCardUtil } from "./id.card.util";
import { CardConfig, CardService, RegexConfig } from "../../../services/card.service";
import { s25Resource } from "../../../util/s25.resource";
import { DBContact } from "../../student-housing/student.housing.service";

@Component({
    standalone: true,
    selector: "s25-ng-id-card-config",
    template: `
        <section class="surface">
            <form class="config-form">
                <div class="form-row">
                    <label for="card-config--name">Name</label>
                    <input id="card-config--name" type="text" [formField]="configForm.name" />
                    @for (error of configForm.name().errors(); track $index) {
                        <p class="field-error">{{ error.message }}</p>
                    }
                </div>
                <div class="form-row">
                    <label for="card-config--description">Description</label>
                    <textarea id="card-config--description" [formField]="configForm.description"></textarea>
                    @for (error of configForm.description().errors(); track $index) {
                        <p class="field-error">{{ error.message }}</p>
                    }
                </div>
            </form>
        </section>

        <section>
            <div class="surface">
                <h3>Test Cards</h3>
                @if (!hasTracksConfig()) {
                    <p class="hint">
                        25Live will infer the format of the first card that you add to populate the configuration
                    </p>
                }
            </div>
            <div class="test-cards">
                @for (card of cards(); track $index) {
                    <div class="test-card--wrapper">
                        <div #testCard class="test-card" [class.editing]="card.editing()">
                            <div class="stripe">
                                <h4 class="title">Card {{ $index + 1 }}</h4>
                                @if (!card.editing()) {
                                    <h5 class="card-id">
                                        <span class="label">ID:</span>
                                        @if (card.id()) {
                                            <span class="id"> {{ card.id() }}</span>
                                        } @else {
                                            <span class="error">Not Found</span>
                                        }
                                    </h5>
                                    @if (card.contact()) {
                                        <p class="card-contact">{{ card.contact()?.fullName }}</p>
                                    }
                                }
                            </div>
                            <div class="tracks">
                                @for (track of card.tracks(); track $index; let t = $index) {
                                    <p class="track-label">
                                        Track {{ $index + 1 }}:
                                        @if (!card.editing() && !track.matches) {
                                            <span class="error">Does not match</span>
                                        }
                                    </p>
                                    @if (card.editing()) {
                                        <input type="text" class="c-input" [formField]="card.form[$index]" />
                                    } @else if (!track.empty) {
                                        @let trackMeta = meta()[t];
                                        <p
                                            class="track"
                                            [style.--fields-total]="trackMeta.fieldCount"
                                            [attr.data-matches]="track.matches"
                                        >
                                            @for (part of track.parts; track $index; let p = $index) {
                                                @let partMeta = trackMeta.parts[p];
                                                <div class="part-wrapper">
                                                    <div class="part-label">
                                                        {{ partMeta.part.label }}
                                                    </div>
                                                    <s25-ng-button
                                                        [attr.data-part-type]="track.matches ? partMeta.type : 'none'"
                                                        [style.--fields-i]="partMeta?.fieldIndex"
                                                        [type]="'none'"
                                                        class="part"
                                                        (clicked)="editingPart.set({ t, p })"
                                                    >
                                                        {{ part }}
                                                    </s25-ng-button>
                                                </div>
                                            }
                                        </p>
                                    }
                                }
                            </div>
                        </div>
                        <div class="buttons">
                            <s25-ng-button [type]="'primary'" (clicked)="toggleCardEdit(card, testCard)"
                                >{{ card.editing() ? "Save" : "Edit" }}
                            </s25-ng-button>
                            @if (!card.editing() && !card.matches()) {
                                <s25-ng-button [type]="'outline'" (clicked)="guessFrom(card)"> Infer</s25-ng-button>
                            }
                            <s25-ng-button [type]="'danger--outline'" (clicked)="removeCard($index)">
                                Remove
                            </s25-ng-button>
                        </div>
                    </div>
                }
                <s25-ng-button class="add-card" [type]="'none'" [ariaLabel]="'Add Card'" (clicked)="addManualCard()">
                    <div class="test-card">
                        <div class="stripe">
                            <h4 class="title">Add Card</h4>
                        </div>
                        <div class="content">
                            <s25-ng-icon [type]="'plus'" [ariaLabel]="'Add Example'" />
                            <p>Swipe a card or click here to add one manually</p>
                        </div>
                    </div>
                </s25-ng-button>
            </div>
        </section>

        @if (hasTracksConfig()) {
            <section class="surface">
                <h3>Configuration</h3>
                <p class="hint">Identify what fields are included on each track and how they are formatted</p>
                <p class="hint">25Live currently only uses the Internal ID, any other fields may be left unmapped</p>

                <div class="track-config">
                    @for (track of configForm.tracks; track track; let t = $index) {
                        @let trackMeta = meta()![t];
                        <div class="track-wrapper">
                            <p class="track-label">Track {{ t + 1 }}</p>
                            <div class="track" [style.--fields-total]="trackMeta.fieldCount">
                                @for (part of track; track part; let p = $index) {
                                    @let partMeta = trackMeta.parts[p];
                                    <s25-ng-button
                                        [type]="'none'"
                                        class="part"
                                        [attr.data-valid]="part().valid()"
                                        [attr.data-part-type]="partMeta.type"
                                        [style.--fields-i]="partMeta.fieldIndex"
                                        (clicked)="editingPart.set({ t, p })"
                                    >
                                        {{ part().value().label }}
                                    </s25-ng-button>
                                }
                                <s25-ng-icon-button
                                    class="add-part"
                                    [type]="'plus'"
                                    [ariaLabel]="'Add part'"
                                    (clicked)="addPart(t)"
                                />
                            </div>
                        </div>
                    }
                </div>

                @if (editingPart()) {
                    <hr />
                    <section class="edit-part">
                        @let t = editingPart()!.t;
                        @let p = editingPart()!.p;
                        @let form = configForm.tracks[t][p];
                        @let trackMeta = meta()![t];
                        @let partMeta = trackMeta.parts[p];
                        <div class="header">
                            <h4 class="title">
                                Edit
                                <span
                                    class="part"
                                    [attr.data-part-type]="partMeta.type"
                                    [style.--fields-i]="partMeta.fieldIndex"
                                    [style.--fields-total]="trackMeta.fieldCount"
                                >
                                    {{ form.label().value() }}
                                </span>
                            </h4>
                            <div class="buttons">
                                <s25-ng-icon-button
                                    [type]="'arrowLeft'"
                                    [ariaLabel]="'Move part left'"
                                    [disabled]="p <= 0"
                                    (clicked)="movePart(t, p, 'left')"
                                />
                                <s25-ng-icon-button
                                    class="delete"
                                    [type]="'trash2'"
                                    [ariaLabel]="'Remove part'"
                                    (clicked)="removePart(t, p)"
                                />
                                <s25-ng-icon-button
                                    [type]="'arrowRight'"
                                    [ariaLabel]="'Move part right'"
                                    [disabled]="p >= configForm.tracks().value()[t].length - 1"
                                    (clicked)="movePart(t, p, 'right')"
                                />
                            </div>
                        </div>
                        <form class="part-form" action="javascript:void(0);">
                            <div class="part-form--row" data-field="label">
                                <s25-ng-info-message [isPopover]="true">
                                    Label the field so that you can remember what it is
                                </s25-ng-info-message>
                                <label> Label </label>
                                <div>
                                    <input type="text" class="c-input" [formField]="form.label" />
                                    @for (error of form.label().errors(); track $index) {
                                        <p class="field-error">{{ error.message }}</p>
                                    }
                                </div>
                            </div>

                            <div class="part-form--row" data-field="exact">
                                <s25-ng-info-message [isPopover]="true">
                                    Check if the field always has the exact same value
                                </s25-ng-info-message>
                                <label> Fixed Value </label>
                                <div>
                                    <s25-ng-checkbox [formField]="form.exact.selected" />
                                    @if (!form.exact.text().hidden()) {
                                        <input
                                            type="text"
                                            class="c-input"
                                            [formField]="form.exact.text"
                                            placeholder="Enter value"
                                        />
                                    }
                                    @for (error of form.exact.text().errors(); track $index) {
                                        <p class="field-error">{{ error.message }}</p>
                                    }
                                </div>
                            </div>

                            @if (!form.length().hidden()) {
                                <div class="part-form--row" data-field="length">
                                    <s25-ng-info-message [isPopover]="true">
                                        Does the field always have a specified length or range of lengths?
                                    </s25-ng-info-message>
                                    <label> Length </label>
                                    <div>
                                        <select class="cn-form__control" [formField]="form.length.type">
                                            <option value="any">Any</option>
                                            <option value="fixed">Fixed</option>
                                            <option value="range">Variable</option>
                                        </select>
                                        <div class="range">
                                            @if (!form.length.min().hidden()) {
                                                <input
                                                    type="number"
                                                    class="c-input"
                                                    min="0"
                                                    [formField]="form.length.min"
                                                />
                                            }
                                            @if (!form.length.max().hidden()) {
                                                <span> to </span>
                                                <input
                                                    type="number"
                                                    class="c-input"
                                                    min="0"
                                                    [formField]="form.length.max"
                                                />
                                            }
                                            @if (form.length.type().value() !== "any") {
                                                <span>characters</span>
                                            }
                                        </div>
                                        @for (error of form.length().errorSummary(); track $index) {
                                            <p class="field-error">{{ error.message }}</p>
                                        }
                                    </div>
                                </div>
                            }

                            @if (!form.characterSets().hidden()) {
                                <div class="part-form--row" data-field="characterSets">
                                    <s25-ng-info-message [isPopover]="true">
                                        What characters does the field consist of?
                                    </s25-ng-info-message>
                                    <label> Allowed Characters </label>
                                    <div>
                                        <s25-ng-checkbox [formField]="form.characterSets.letters"
                                            >Letters</s25-ng-checkbox
                                        >
                                        <s25-ng-checkbox [formField]="form.characterSets.numbers"
                                            >Numbers</s25-ng-checkbox
                                        >
                                        <div class="specific">
                                            <s25-ng-checkbox [formField]="form.characterSets.specific.selected">
                                                Specific
                                            </s25-ng-checkbox>

                                            @if (!form.characterSets.specific.characters().hidden()) {
                                                <input
                                                    type="text"
                                                    class="c-input"
                                                    placeholder="Enter characters"
                                                    [formField]="form.characterSets.specific.characters"
                                                />
                                            }
                                        </div>
                                        @for (error of form.characterSets().errorSummary(); track $index) {
                                            <p class="field-error">{{ error.message }}</p>
                                        }
                                    </div>
                                </div>
                            }

                            @if (!form.optional().hidden()) {
                                <div class="part-form--row" data-field="optional">
                                    <s25-ng-info-message [isPopover]="true">
                                        Check if the field is not always present
                                    </s25-ng-info-message>
                                    <label> Optional </label>
                                    <s25-ng-checkbox [formField]="form.optional" />
                                </div>
                            }

                            @if (!form.mapTo().hidden()) {
                                <div class="part-form--row" data-field="mapTo">
                                    <s25-ng-info-message [isPopover]="true">
                                        What value does this field map to in 25Live?
                                    </s25-ng-info-message>
                                    <label> Map To </label>
                                    <select class="cn-form__control" [formField]="form.mapTo">
                                        <option value="none">None</option>
                                        <option value="internalId">Internal ID</option>
                                    </select>
                                </div>
                            }
                        </form>
                    </section>
                }
            </section>
        }
        <div class="footer buttons">
            <s25-ng-button
                [type]="'primary'"
                [onClick]="save"
                [disabled]="!configForm().valid() || !configForm().dirty()"
            >
                Save
            </s25-ng-button>
            <s25-ng-button [type]="'outline'" (clicked)="cancel()">Cancel</s25-ng-button>
            @if (!isNew()) {
                <s25-ng-button [type]="'danger--outline'" [onClick]="delete">Delete</s25-ng-button>
            }
        </div>
    `,
    styles: `
        /*
            Layout
        */
        :host {
            display: grid;
            row-gap: 2rem;
            --gold: #ffe300;
        }

        h3,
        h4,
        h5 {
            margin: 0;
        }

        p {
            font-size: 0.9em;
        }

        .hint {
            color: var(--color--text-muted);
        }

        label {
            font-weight: bold;
        }

        input[type="text"],
        input[type="number"],
        textarea {
            border: 1px solid var(--color--border) !important;
            border-radius: 6px;
            font-size: 0.875rem;
            background: var(--color--surface-bg);
            padding: 0.5rem 0.75rem;
            color: var(--color--text);
        }

        select {
            border: 1px solid var(--color--border) !important;
            width: fit-content;
        }

        textarea {
            min-height: 6rem;
            resize: vertical;
        }

        input[type="number"] {
            width: 10ch;
        }

        .surface {
            background: var(--color--surface-bg);
            padding: 1rem;
            border-radius: 6px;
            border: 1px solid var(--color--border);
        }

        .buttons {
            display: flex;
            justify-content: center;
            gap: 0.5rem;
        }

        .footer.buttons {
            justify-content: start;
        }

        /*
            First section (name/description)
        */
        .config-form {
            display: grid;
            row-gap: 1rem;
        }

        .form-row {
            display: grid;
            row-gap: 0.25rem;
        }

        .field-error {
            color: var(--error-message-color);
            font-size: 0.9em;
            grid-column: 1/-1;
        }

        /*
            Test cards
        */
        .test-cards {
            --card-height: 18rem;
            display: grid;
            grid-template-columns: repeat(auto-fill, calc(var(--card-height) * 27 / 17));
            gap: 0.5rem;
            padding-block: 1rem;
        }

        .test-card {
            display: flex;
            flex-direction: column;
            background-color: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            aspect-ratio: 27/17;
            box-shadow:
                1px 1px 0 #dadada,
                3px 3px 9px #0000008c;
            border-radius: 1rem;
            padding-top: 4%;
            transition: rotate 200ms ease-in-out;
            height: var(--card-height);
        }

        .test-card.rotateX90 {
            rotate: x 90deg;
        }

        .test-card .stripe {
            height: 24%;
            align-content: center;
            text-align: center;
            container-type: size;
            overflow: hidden;
            padding-inline: 0.5rem;
        }

        .test-card:not(.editing) .stripe {
            background: #474747;
            color: white;
        }

        .test-card .title {
            font-weight: bold;
            text-align: center;
            font-size: 1em !important;
        }

        .test-card .card-id {
            font-size: 1em !important;
            text-align: center;
            overflow: hidden;
            text-overflow: ellipsis;
            color: var(--gold);
            display: flex;
            column-gap: 0.5rem;
            justify-content: center;
        }

        .test-card .card-id .label {
            color: white;
        }

        .test-card .card-id .id {
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .test-card .error {
            color: var(--error-message-color);
            font-size: 0.9em;
            grid-column: 1/-1;
        }

        .test-cards .tracks {
            display: grid;
            row-gap: 0.25rem;
            padding: 0.5rem;
        }

        .test-card .part-wrapper {
            position: relative;
            overflow-wrap: anywhere;
        }

        .test-card .part-label {
            position: absolute;
            top: -0.3em;
            left: 50%;
            translate: -50% -100%;
            border-radius: 6px;
            padding: 0 0.2rem;
            background: white;
            visibility: hidden;
            opacity: 0;
            transition: opacity 400ms ease-in-out;
            width: max-content;
        }

        .test-card .part-wrapper:hover .part-label {
            visibility: visible;
            opacity: 1;
        }

        .test-cards .buttons {
            padding-block-start: 0.5rem;
        }

        .test-cards .add-card {
            opacity: 0.5;
            height: var(--card-height);

            .title {
                font-size: 1.25em !important;
            }

            .content {
                flex-grow: 1;
                text-align: center;
                align-content: center;
                font-size: 1.25em;
            }

            s25-ng-icon {
                font-size: 3em;
                color: var(--cn-blue);
            }
        }

        /*
            Tracks (both test cards and config)
         */
        .track-label {
            font-weight: bold;
            color: var(--color--text);
        }

        .track {
            display: flex;
            flex-wrap: wrap;
            gap: 0.2rem;
        }

        .part {
            --part-bg: #e5e5e5;
            --part-color: black;
            background: var(--part-bg);
            color: var(--part-color);
            padding: 0.1em 0.25em;
            border-radius: 1em;
            padding-block-start: 0.2em;
            text-align: center;
            min-width: 1rem;
        }

        .part[data-valid="false"] {
            outline: 1px solid var(--error-message-color);
            box-shadow: 0 0 0.5rem var(--error-message-color);
        }

        .part[data-part-type="field"] {
            --part-bg: hsl(
                from var(--cn-blue) calc(h + var(--fields-i) * 360 / var(--fields-total)) s l / 1
            ) !important;
            --part-color: white !important;
        }

        .part[data-part-type="internalId"] {
            --part-bg: var(--gold);
            box-shadow: 0 0 18px 0 var(--gold);
            background: hsl(from var(--part-bg) h s l / 60%);
        }

        /*
            Track configuration display
        */
        .track-config {
            display: grid;
            row-gap: 0.5rem;
            padding-block-start: 1rem;
        }

        .track-config .track-wrapper {
            display: grid;
            row-gap: 0.25rem;
        }

        /*
            Track configuration edit
        */
        .edit-part .title {
            font-size: 1.1rem !important;
        }

        .edit-part .header {
            display: flex;
            gap: 1rem;
            justify-content: space-between;
            padding-block-end: 1rem;
            align-items: center;
        }

        .edit-part .delete {
            --color--link-text: var(--color--danger);
        }

        .part-form {
            display: grid;
            grid-template-columns: 2rem 19ch 1fr;
            row-gap: 0.75rem;
        }

        .part-form--row {
            display: grid;
            grid-template-columns: subgrid;
            grid-column: 1/-1;
            align-items: center;
        }

        .part-form--row[data-field="label"] input {
            max-width: 20ch;
        }

        .part-form--row[data-field="characterSets"] > div {
            display: grid;
        }

        .part-form--row[data-field="characterSets"] .specific {
            display: flex;
            gap: 0.5rem;
        }

        .part-form .range {
            display: flex;
            gap: 0.5rem;
            align-items: center;
            padding-block-start: 0.25rem;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25DropdownPaginatedModule,
        FormField,
        S25ToggleButtonModule,
        S25ButtonComponent,
        S25CheckboxModule,
        S25IconButtonComponent,
        S25IconComponent,
        ReactiveFormsModule,
        S25RadioModule,
        S25InfoMessageModule,
    ],
    providers: [CardReaderService],
})
export class IdCardConfigComponent {
    readonly config = input.required<CardConfig>();

    readonly done = output<void>();

    readonly elementRef = inject(ElementRef);
    readonly injector = inject(Injector);
    readonly store = inject(IdCardsStore);
    readonly s25Util = S25Util;
    readonly modalService = ModalService;
    readonly cardReader = inject(CardReaderService);
    readonly cardService = inject(CardService);

    private static NAME_MAX_LENGTH = 128;
    private static REGEX_LABEL_MAX_LENGTH = 64;
    private static REGEX_SPECIFIC_MAX_LENGTH = 256;
    private static REGEX_EXACT_MAX_LENGTH = 256;

    readonly cardSwipe$ = this.cardReader.swipe$.pipe(
        filter(() => this.elementRef.nativeElement.checkVisibility()),
        takeUntilDestroyed(),
    );

    readonly configModel = linkedSignal(() => this.config());
    readonly configForm = form(this.configModel, (schema) => {
        validate(schema.tracks, ({ value }) => {
            if (!value().length) {
                return { kind: "field-length", message: "At least one track is required" };
            }
        });
        validate(schema.name, (field) => {
            const name = field.value();
            if (!name) return { kind: "empty-field", message: "Name is required" };
            if (name.length > IdCardConfigComponent.NAME_MAX_LENGTH) {
                return {
                    kind: "field-length",
                    message: `Name must be ${IdCardConfigComponent.NAME_MAX_LENGTH} characters or less`,
                };
            }
            if (this.store.isNameTaken(name, this.configModel().id)) {
                return {
                    kind: "duplicate-name",
                    message: "Name is already taken",
                };
            }
        });
        applyEach(schema.tracks, (trackSchema) => {
            applyEach(trackSchema, (partSchema) => {
                hidden(partSchema.exact.text, ({ valueOf }) => !valueOf(partSchema.exact.selected));
                hidden(partSchema.length, ({ valueOf }) => valueOf(partSchema.exact.selected));
                hidden(partSchema.length.min, ({ valueOf }) => valueOf(partSchema.length.type) === "any");
                hidden(partSchema.length.max, ({ valueOf }) => valueOf(partSchema.length.type) !== "range");
                hidden(partSchema.characterSets, ({ valueOf }) => valueOf(partSchema.exact.selected));
                hidden(partSchema.mapTo, ({ valueOf }) => valueOf(partSchema.exact.selected));
                hidden(
                    partSchema.characterSets.specific.characters,
                    ({ valueOf }) => !valueOf(partSchema.characterSets.specific.selected),
                );
                hidden(
                    partSchema.optional,
                    ({ valueOf }) => valueOf(partSchema.exact.selected) && !valueOf(partSchema.exact.text),
                );
                validate(partSchema.label, ({ value }) => {
                    if (value().length > IdCardConfigComponent.REGEX_LABEL_MAX_LENGTH) {
                        return {
                            kind: "field-length",
                            message: `Label must be ${IdCardConfigComponent.REGEX_LABEL_MAX_LENGTH} characters or less`,
                        };
                    }
                });
                validate(partSchema.length.min, ({ value, valueOf }) => {
                    const label = valueOf(partSchema.length.type) === "fixed" ? "Length" : "Min length";
                    const errors: ValidationError[] = [];

                    if (!this.s25Util.isInt(value())) {
                        errors.push({
                            kind: "field-empty",
                            message: `${label} must be a valid integer`,
                        });
                    }

                    if (value() < 0) {
                        errors.push({
                            kind: "field-length",
                            message: `${label} cannot be negative`,
                        });
                    }

                    return errors;
                });
                validate(partSchema.length.max, ({ value, valueOf }) => {
                    const errors: ValidationError[] = [];

                    if (!this.s25Util.isInt(value())) {
                        errors.push({
                            kind: "field-empty",
                            message: `Max length must be a valid integer`,
                        });
                    }

                    if (value() < 0) {
                        errors.push({
                            kind: "field-length",
                            message: "Max length cannot be negative",
                        });
                    }
                    if (valueOf(partSchema.length.min) > value()) {
                        errors.push({
                            kind: "field-length",
                            message: `Max length cannot be greater than the min length`,
                        });
                    }

                    return errors;
                });
                validate(partSchema.characterSets.specific.characters, ({ value }) => {
                    const errors: ValidationError[] = [];

                    const invalidCharacters = value().replace(/[A-Z0-9 !"#%&'()*+,\-./:;<=>^]/gi, "");
                    if (invalidCharacters.length) {
                        errors.push({
                            kind: "field-characters",
                            message: `Invalid characters: ${this.s25Util.array.unique(invalidCharacters.split(""))}`,
                        });
                    }

                    const unique = this.s25Util.array.unique(value().split(""));
                    if (unique.length !== value().length) {
                        errors.push({
                            kind: "duplicate-characters",
                            message: "Specific characters must be unique",
                        });
                    }

                    if (value().length > IdCardConfigComponent.REGEX_SPECIFIC_MAX_LENGTH) {
                        errors.push({
                            kind: "field-length",
                            message: `Specific characters must be ${IdCardConfigComponent.REGEX_SPECIFIC_MAX_LENGTH} characters or less`,
                        });
                    }

                    return errors;
                });
                validate(partSchema.exact.text, ({ value }) => {
                    const errors: ValidationError[] = [];

                    const invalidCharacters = value().replace(/[A-Z0-9 !"#%&'()*+,\-./:;<=>^]/gi, "");
                    if (invalidCharacters.length) {
                        errors.push({
                            kind: "field-characters",
                            message: `Invalid characters: ${this.s25Util.array.unique(invalidCharacters.split(""))}`,
                        });
                    }

                    if (value().length > IdCardConfigComponent.REGEX_EXACT_MAX_LENGTH) {
                        errors.push({
                            kind: "field-length",
                            message: `Fixed value must be ${IdCardConfigComponent.REGEX_EXACT_MAX_LENGTH} characters or less`,
                        });
                    }

                    return errors;
                });
            });
        });
    });
    readonly hasTracksConfig = computed(() => this.configForm.tracks().value()?.length > 0);

    readonly isNew = computed(() => !this.configModel().id);
    readonly cards = signal<Card[]>([]);
    readonly editingPart = signal<{ t: number; p: number } | undefined>(undefined);

    readonly trackRegexes = computed<RegExp[]>(() => {
        const tracks = this.configForm.tracks().value();
        if (!tracks.length) return [/(.+)/, /(.+)/, /(.+)/];

        return tracks.map((track) => {
            const re = track.map((part) => IdCardUtil.partToRegex(part)).join("");
            return new RegExp(`^${re}$`);
        });
    });

    readonly meta = computed(() => {
        const tracks = this.configForm.tracks().value();
        return tracks.map((track) => {
            let fieldCount = 0;
            return {
                parts: track.map((part) => {
                    const type = part.mapTo !== "none" ? part.mapTo : part.exact.selected ? "none" : "field";
                    const fieldIndex = type === "none" ? undefined : fieldCount++;
                    return { type, fieldIndex, part };
                }),
                fieldCount: fieldCount,
            };
        });
    });

    constructor() {
        this.cardSwipe$.subscribe((card) => this.onCardSwipe(card));
    }

    onCardSwipe(card: CardSwipe): void {
        if (card.error) {
            NotificationService.post("Error reading card", 3 * S25Const.ms.sec);
            return;
        }

        // Check if card already exists before adding it
        const exists = this.cards().some((c) => c.plainTracks().every((t, i) => t === card.tracks[i].value));
        if (exists) {
            NotificationService.post("Card already present", 3 * S25Const.ms.sec);
            return;
        }
        const newCard = this.processCard(card.tracks.map((t) => t.value));
        this.cards.update((cards) => [...cards, newCard]);
        this.guessIfNoConfig();
    }

    processCard(card: string[]): Card {
        const cardModel = signal(card);
        const cardForm = untracked(() => runInInjectionContext(this.injector, () => form(cardModel)));
        const plainTracks = signal(card);

        const tracks = computed<Track[]>(() => {
            const tracks = plainTracks();
            const regexes = this.trackRegexes();

            return tracks.map((track, t) => {
                const regex = regexes[t];
                const parts = track.match(regex)?.slice(1);
                return { matches: !!parts, empty: !track.length, parts: parts ?? [track] };
            });
        });

        const id = computed(() => {
            const config = this.configForm.tracks().value();
            const part = config
                ?.flatMap((track, t) => track.map((part, p) => ({ t, p, part })))
                .filter(({ part }) => part.mapTo === "internalId")[0];
            if (!part) return;
            return tracks()[part.t].parts[part.p];
        });

        const contact = untracked(() =>
            runInInjectionContext(this.injector, () =>
                s25Resource({
                    params: () => id(),
                    loader: async ({ params: internalId, abortSignal }) => {
                        return this.cardService.getContactFromInternalId(internalId);
                    },
                }),
            ),
        );

        return {
            plainTracks,
            editing: signal(false),
            model: cardModel,
            form: cardForm,
            tracks,
            matches: computed(() => tracks().every((t) => t.matches)),
            id,
            contact: contact.value,
        };
    }

    async toggleCardEdit(card: Card, elem: HTMLElement) {
        elem.classList.add("rotateX90");
        await this.s25Util.delay(200);
        if (card.editing()) {
            card.plainTracks.set(card.model().map((value) => value.toUpperCase()));
            card.editing.set(false);
            this.guessIfNoConfig();
        } else {
            card.editing.set(true);
        }
        elem.classList.remove("rotateX90");
    }

    addManualCard() {
        const newCard = this.processCard(["", "", ""]);
        newCard.editing.set(true);
        this.cards.update((cards) => [...cards, newCard]);
    }

    removeCard(index: number) {
        this.cards.update((cards) => cards.filter((_, i) => i !== index));
    }

    async guessFrom(card: Card) {
        const ok = await this.modalService.dialog({
            type: "Yes No",
            title: "Confirm",
            message: `This action will overwrite your current configuration with a guessed format based on the selected card.<br><br><strong>Are you sure that you want to proceed?</strong>`,
            primary: "Yes",
        });
        if (!ok) return;
        this.configModel.update((model) => ({ ...model, tracks: this.guess(card.plainTracks()) }));
    }

    guessIfNoConfig() {
        if (this.hasTracksConfig()) return;
        const cards = this.cards();
        const card = cards.find((card) => !card.editing() && card.plainTracks().some((track) => track.length > 0));
        const tracks = card?.plainTracks();
        if (!tracks?.length) return;
        this.configModel.update((model) => ({ ...model, tracks: this.guess(tracks) }));
        this.configForm().markAsDirty();
    }

    guess(tracks: string[]): RegexConfig[][] {
        return tracks.map((track, i) => {
            return (
                track
                    .split(/(?=[!"#%&'()*+,\-./:;<=>^]|(?<=[!"#%&'()*+,\-./:;<=>^]))/g)
                    // Commenting this out for now. Might be more annoying than helpful
                    // .flatMap((part) => {
                    //     // If part contains a set of letters followed by a set of numbers, or vice versa, split it into two parts
                    //     const parts = part.split(/(?<=[A-Z])(?=\d)|(?<=\d)(?=[A-Z])/);
                    //     if (parts.length === 2) return parts;
                    //     return part;
                    // })
                    .map((part) => {
                        const hasLetters = /[A-Z]/.test(part);
                        const hasNumbers = /[0-9]/.test(part);
                        const symbols = this.s25Util.array.unique(part.replace(/[A-Z0-9]/g, "").split("")).join("");
                        const empty = !track.length;
                        const exact = symbols.length === 1;
                        const label = empty ? "Empty Track" : exact ? part : "unknown";
                        return <RegexConfig>{
                            label: label,
                            length: { type: "any", min: part.length, max: part.length },
                            optional: false,
                            characterSets: {
                                letters: hasLetters,
                                numbers: hasNumbers,
                                specific: { selected: !!symbols.length, characters: symbols },
                            },
                            exact: { selected: empty || exact, text: part },
                            mapTo: "none",
                        };
                    })
            );
        });
    }

    addPart(trackIndex: number) {
        this.configModel.update((model) => ({
            ...model,
            tracks: model.tracks.map((track, t) => {
                if (t !== trackIndex) return track;
                return [
                    ...track,
                    {
                        label: "unknown",
                        length: { type: "any", min: 6, max: 6 },
                        optional: false,
                        characterSets: {
                            letters: true,
                            numbers: true,
                            specific: { selected: false, characters: "" },
                        },
                        exact: { selected: false, text: "" },
                        mapTo: "none",
                    },
                ];
            }),
        }));
        this.editingPart.set({ t: trackIndex, p: this.meta()[trackIndex].parts.length - 1 });
    }

    movePart(t: number, p: number, dir: "left" | "right") {
        const di = dir === "left" ? -1 : 1;
        const newIndex = p + di;
        if (newIndex < 0 || newIndex >= this.configForm.tracks().value().length) return;

        this.configModel.update((model) => {
            const newTracks = [...model.tracks];
            [newTracks[t][p], newTracks[t][newIndex]] = [newTracks[t][newIndex], newTracks[t][p]];
            return { ...model, tracks: newTracks };
        });
        this.editingPart.update((part) => ({ ...part!, p: newIndex }));
    }

    removePart(t: number, p: number) {
        this.configModel.update((model) => {
            const newTracks = [...model.tracks];
            newTracks[t].splice(p, 1);
            return { ...model, tracks: newTracks };
        });
        this.editingPart.set(undefined);
    }

    cancel() {
        this.done.emit();
    }

    @Bind
    async save() {
        if (!this.configForm().valid()) return;

        const configModel = this.configModel();
        if (!configModel) return;

        const tracks = this.configForm().value().tracks;
        if (!tracks?.length) return;

        const ok = await this.store.addConfig({
            id: configModel.id,
            name: configModel.name,
            description: configModel.description,
            tracks,
        });

        if (ok) this.done.emit();
    }

    @Bind
    async delete() {
        const configModel = this.configModel();
        if (!configModel || !configModel.id) return;

        const ok = await this.modalService.dialog({
            type: "Yes No",
            primary: "No",
            title: "Confirm Deletion",
            message: "Are you sure that you want to delete this card config?",
        });
        if (!ok) return;
        await this.store.deleteConfig(configModel.id);
        this.done.emit();
    }
}

type Card = {
    plainTracks: WritableSignal<string[]>;
    editing: WritableSignal<boolean>;
    model: WritableSignal<string[]>;
    form: FieldTree<string[]>;
    tracks: Signal<Track[]>;
    id: Signal<string | undefined>;
    matches: Signal<boolean>;
    contact: Signal<Pick<DBContact, "contId" | "fullName"> | undefined>;
};
type Track = { parts: string[]; matches: boolean; empty: boolean };
